import type { AuthenticatedUserDto } from '@src/features/users';
import settings from '@src/shared/config';
import {
  convertPythonTimestampToJsTimestamp,
  getTimezone,
} from '@src/shared/lib/dateUtils';
import { parseJwt } from '@src/shared/lib/jwtUtils';
import {
  isTemporaryAuthStatus,
  TemporaryAuthError,
} from '../lib/errors';

type TokenMetadata = {
  accessTokenExpiredAt: number | null;
  refreshTokenExpiredAt: number | null;
};

type JwtCredentials = {
  access: string | null;
  refresh: string | null;
  metadata: TokenMetadata;
  user: AuthenticatedUserDto | null;
  loginAs: boolean;
};

type Enrollment2FAEmailSent = {
  two_factor_enrollment_email_sent: boolean,
  detail: string,
}

type TwoFactorChallengeToken = {
  two_factor_required: boolean,
  challenge_token: string
}

type Token2FAEnrollment = {
  two_factor_required: boolean,
  enrollment_required: boolean,
  challenge_token: string,
  provisioning_uri: string,
  qr_code_data_url: string,
  manual_entry_key: string,
}

type AuthCredentials = (
  JwtCredentials
  | Enrollment2FAEmailSent
  | TwoFactorChallengeToken
  | null);

type JwtStorage = {
  set: (payload: JwtCredentials) => void;
  remove: () => void;
  parse: () => JwtCredentials | null;
};

type TokenPayload = {
  exp?: number;
};

type AuthData = Record<string, unknown>;

type JwtProviderParams = {
  storage: JwtStorage;
};

class BaseAuthProvider {
  get isAuthenticated(): boolean {
    throw Error('The isAuthenticated getter is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  async login(_username: string, _password: string): Promise<AuthCredentials> {
    throw Error('The login method is not implemented.');
  }

    // eslint-disable-next-line no-unused-vars
  async verifyTwoFactor(_challengeToken: string, _otpCode: string): Promise<JwtCredentials | null> {
    throw Error('The verifyTwoFactor method is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  async startTwoFactorEnrollment(_token: string): Promise<AuthCredentials> {
    throw Error('The startTwoFactorEnrollment method is not implemented.');
  }

  async logout(): Promise<AuthCredentials> {
    throw Error('The logout method is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  authenticate(_data: AuthData): AuthCredentials {
    throw Error('The authenticate method is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  updateUser(
    _user: Partial<AuthenticatedUserDto>,
  ): JwtCredentials | null {
    throw Error('The updateUser method is not implemented.');
  }

  unauthenticate(): AuthCredentials {
    throw Error('The unauthenticate method is not implemented.');
  }
}

class BaseJwtAuthProvider extends BaseAuthProvider {
  storage: JwtStorage;

  constructor({ storage }: JwtProviderParams) {
    super();
    this.storage = storage;
  }

  get credentials(): JwtCredentials | null {
    return this.storage.parse();
  }

  get config() {
    return settings.AUTH.JWT;
  }

  get isAuthenticated() {
    return !this.isAccessTokenExpired && Boolean(this.credentials);
  }

  get isAccessTokenExpired() {
    const expiredAt = this.credentials?.metadata?.accessTokenExpiredAt;

    if (expiredAt === null || expiredAt === undefined) {
      return true;
    }

    const delta = expiredAt - Date.now();
    return delta < this.config.EXPIRED_BUFFER;
  }

  get isRefreshTokenExpired() {
    const expiredAt = this.credentials?.metadata?.refreshTokenExpiredAt;

    if (expiredAt === null || expiredAt === undefined) {
      return true;
    }

    const delta = expiredAt - Date.now();
    return delta < this.config.EXPIRED_BUFFER;
  }

  authenticate(data: AuthData) {
    const credentials = this._createCredentials(data);
    return this._setCredentials(credentials);
  }

  updateUser(
    user: Partial<AuthenticatedUserDto>,
  ): JwtCredentials | null {
    const credentials = this.credentials;
    if (!credentials?.user) {
      return credentials;
    }

    return this._setCredentials({
      ...credentials,
      user: {
        ...credentials.user,
        ...user,
      },
    });
  }

  unauthenticate() {
    return this._removeCredentials();
  }

  _createCredentials(data: AuthData): JwtCredentials {
    const access =
      (data[this.config.ACCESS_TOKEN_FIELD] as string | null) ?? null;
    const refresh =
      (data[this.config.REFRESH_TOKEN_FIELD] as string | null) ?? null;
    const user =
      (data[this.config.USER_FIELD] as
        | AuthenticatedUserDto
        | null
        | undefined) ?? null;
    return {
      access,
      refresh,
      metadata: {
        accessTokenExpiredAt: this._parseTokenExpiredAt(access),
        refreshTokenExpiredAt: this._parseTokenExpiredAt(refresh),
      },
      user,
      loginAs: !!(data.loginAs || this.credentials?.loginAs),
    };
  }

  _setCredentials(credentials: JwtCredentials): JwtCredentials {
    this.storage.set(credentials);
    return credentials;
  }

  _removeCredentials(): AuthCredentials {
    this.storage.remove();
    return null;
  }

  _parseToken(token: string | null): TokenPayload {
    let data: TokenPayload = {};
    try {
      if (token) {
        data = parseJwt(token) as TokenPayload;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return data;
  }

  _parseTokenExpiredAt(token: string | null) {
    const data = this._parseToken(token);
    return Number.isFinite(data.exp)
      ? convertPythonTimestampToJsTimestamp(data.exp as number)
      : null;
  }

  // eslint-disable-next-line no-unused-vars
  async createToken(
    _username: string,
    _password: string,
  ): Promise<AuthCredentials> {
    throw Error('The createToken method is not implemented.');
  }

    // eslint-disable-next-line no-unused-vars
  async verifyTwoFactorToken(_challengeToken: string, _otpCode: string): Promise<JwtCredentials | null> {
    throw Error('The verifyTwoFactorToken method is not implemented.');
  }

  // eslint-disable-next-line no-unused-vars
  async startTwoFactorEnrollmentToken(_token: string): Promise<Token2FAEnrollment> {
    throw Error(
      'The startTwoFactorEnrollmentToken method is not implemented.',
    );
  }

  async revokeToken(): Promise<AuthCredentials> {
    throw Error('The revokeToken method is not implemented.');
  }

  async verifyToken(): Promise<AuthCredentials> {
    throw Error('The verifyToken method is not implemented.');
  }

  async refreshToken(): Promise<JwtCredentials> {
    throw Error('The refreshToken method is not implemented.');
  }

  async exchangeImpersonationCode(_code: string): Promise<JwtCredentials> {
    throw Error('The impersonation exchange method is not implemented.');
  }
}

class JwtAuthProvider extends BaseJwtAuthProvider {
  constructor({ storage }: JwtProviderParams) {
    super({ storage });
  }

  async login(username: string, password: string): Promise<AuthCredentials> {
    return await this.createToken(username, password);
  }

  async logout(): Promise<AuthCredentials> {
    return await this.revokeToken();
  }

  async verifyTwoFactor(challengeToken: string | null, otpCode: string): Promise<JwtCredentials | null> {
    return await this.verifyTwoFactorToken(challengeToken, otpCode);
  }

  async startTwoFactorEnrollment(token: string): Promise<Token2FAEnrollment> {
    return await this.startTwoFactorEnrollmentToken(token);
  }


  async createToken(
    username: string,
    password: string,
  ): Promise<AuthCredentials> {
    const url = this.config.TOKEN_CREATE_URL;
    const payload = {
      [this.config.USERNAME_FIELD]: username,
      [this.config.PASSWORD_FIELD]: password,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Login Failed');
      }

      if (data?.two_factor_required || data?.two_factor_enrollment_email_sent) {
        this.unauthenticate();
        return data;
      }

      return this.authenticate(data);
    } catch (error) {
      this.unauthenticate();
      throw error;
    }
  }

  async revokeToken(): Promise<AuthCredentials> {
    return this.unauthenticate();
  }

    async verifyTwoFactorToken(challengeToken: string | null, otpCode: string): Promise<JwtCredentials | null> {
    const url = this.config.TOKEN_2FA_VERIFY_URL;
    const payload = {
      challenge_token: challengeToken,
      otp_code: otpCode,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || '2FA Verification Failed');
      }

      return this.authenticate(data);
    } catch (error) {
      this.unauthenticate();
      throw error;
    }
  }

  async startTwoFactorEnrollmentToken(token: string): Promise<Token2FAEnrollment> {
    const url = this.config.TOKEN_2FA_ENROLLMENT_URL;
    const payload = { token };

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        'User-Timezone': getTimezone(),
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.detail || '2FA Enrollment Init Failed');
    }

    return data;
  }

  async verifyToken(): Promise<AuthCredentials> {
    if (this.isAccessTokenExpired) {
      throw new Error('Verification Failed: Access Token Expired');
    }

    const url = this.config.TOKEN_VERIFY_URL;

    const accessToken = this.credentials?.access;

    if (!accessToken) {
      throw new Error('Verification Failed: Missing Credentials');
    }

    const payload = { token: accessToken };

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });
    } catch {
      throw new TemporaryAuthError(
        'Verification temporarily unavailable',
      );
    }

    if (!response.ok) {
      if (isTemporaryAuthStatus(response.status)) {
        throw new TemporaryAuthError(
          `Verification temporarily unavailable: ${response.status}`,
        );
      }
      throw new Error(`Verification Failed: ${response.status}`);
    }

    return this.credentials;
  }

  async refreshToken(): Promise<JwtCredentials> {
    if (this.isRefreshTokenExpired) {
      throw new Error('Refresh Failed: Refresh Token Expired');
    }

    const url = this.config.TOKEN_REFRESH_URL;
    const refreshToken = this.credentials?.refresh;

    if (!refreshToken) {
      throw new Error('Refresh Failed: Missing Credentials');
    }

    const payload = { refresh: refreshToken };

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'User-Timezone': getTimezone(),
        },
      });

    } catch {
      throw new TemporaryAuthError('Refresh temporarily unavailable');
    }

    if (!response.ok) {
      if (isTemporaryAuthStatus(response.status)) {
        throw new TemporaryAuthError(
          `Refresh temporarily unavailable: ${response.status}`,
        );
      }
      this.unauthenticate();
      throw new Error(`Refresh Failed: ${response.status}`);
    }

    const data = await response.json();
    return this.authenticate(data);
  }

  async exchangeImpersonationCode(code: string): Promise<JwtCredentials> {
    const response = await fetch(this.config.IMPERSONATION_EXCHANGE_URL, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ code }),
      headers: {
        'Content-Type': 'application/json',
        'User-Timezone': getTimezone(),
      },
    });
    if (!response.ok) {
      this.unauthenticate();
      throw new Error(`Impersonation failed: ${response.status}`);
    }

    const data = await response.json();
    return this.authenticate({ ...data, loginAs: true });
  }
}

export { JwtAuthProvider };
export type { AuthData, AuthCredentials, JwtCredentials, JwtProviderParams, Token2FAEnrollment, Enrollment2FAEmailSent };
